/**
 * 라우트 설정
 */

import { Routes, Route, Navigate } from 'react-router';
import { useAuthStore } from '@/stores/auth';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { RoleProtectedRoute } from './RoleProtectedRoute';

// Pages
import { LoginPage } from '@/pages/Login';
import { DashboardPage } from '@/pages/Dashboard';
import { VisitorStatisticsPage } from '@/pages/statistics/VisitorStatistics';
import { AlcoholStatisticsPage } from '@/pages/statistics/AlcoholStatistics';
import { WhiskyListPage } from '@/pages/whisky/WhiskyList';
import { WhiskyDetailPage } from '@/pages/whisky/WhiskyDetail';
import { WhiskyExcelBulkPage } from '@/pages/whisky/WhiskyExcelBulkPage';
import { TastingTagListPage } from '@/pages/tasting-tags/TastingTagList';
import { TastingTagDetailPage } from '@/pages/tasting-tags/TastingTagDetail';
import { TastingGroupListPage } from '@/pages/tasting-groups/TastingGroupList';
import { AlcoholAttributeVariantA } from '@/pages/alcohol-attributes/variants/a/AlcoholAttributeVariantA';
import { BannerListPage } from '@/pages/banners/BannerList';
import { BannerDetailPage } from '@/pages/banners/BannerDetail';
import { BannerCreatePage } from '@/pages/banners/BannerCreate';
import { CampaignContentListPage } from '@/pages/campaign-contents/CampaignContentList';
import { CampaignContentDetailPage } from '@/pages/campaign-contents/CampaignContentDetail';
import { CurationCreateEntry } from '@/pages/curation-spec/CurationCreateEntry';
import { CurationDetail } from '@/pages/curation-spec/CurationDetail';
import { CurationList } from '@/pages/curation-spec/CurationList';
import { InquiryListPage } from '@/pages/inquiries/InquiryList';
import { PolicyListPage } from '@/pages/policies/PolicyList';
import { UserListPage } from '@/pages/users/UserList';
import { ReviewListPage } from '@/pages/reviews/ReviewList';
import { DistilleryListPage } from '@/pages/distilleries/DistilleryList';
import { DistilleryDetailPage } from '@/pages/distilleries/DistilleryDetail';
import { RegionListPage } from '@/pages/regions/RegionList';
import { RegionDetailPage } from '@/pages/regions/RegionDetail';
import { MfdsDeclarationListPage } from '@/pages/mfds/MfdsDeclarationList';
import { MfdsDeclarationDetailPage } from '@/pages/mfds/MfdsDeclarationDetail';
import { MfdsImporterListPage } from '@/pages/mfds/MfdsImporterList';
import { MfdsImporterDetailPage } from '@/pages/mfds/MfdsImporterDetail';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />

      {/* Protected Routes with Admin Layout */}
      <Route
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard */}
        <Route index element={<DashboardPage />} />
        <Route
          path="statistics/visitors"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <VisitorStatisticsPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="statistics/alcohols"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <AlcoholStatisticsPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="dashboard/curations"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN', 'BAR_OWNER', 'COMMUNITY_MANAGER']}>
              <CurationList />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="dashboard/curations/new"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN', 'BAR_OWNER', 'COMMUNITY_MANAGER']}>
              <CurationCreateEntry />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="dashboard/curations/:id"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN', 'BAR_OWNER', 'COMMUNITY_MANAGER']}>
              <CurationDetail />
            </RoleProtectedRoute>
          }
        />

        {/* Whisky - ROOT_ADMIN only */}
        <Route
          path="whisky"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <WhiskyListPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="whisky/new"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <WhiskyDetailPage key="new" />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="whisky/excel-bulk"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <WhiskyExcelBulkPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="whisky/:id"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <WhiskyDetailPage key="edit" />
            </RoleProtectedRoute>
          }
        />

        {/* Tasting Tags - ROOT_ADMIN only */}
        <Route
          path="tasting-tags"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <TastingTagListPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="tasting-tags/new"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <TastingTagDetailPage key="new" />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="tasting-tags/:id"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <TastingTagDetailPage key="edit" />
            </RoleProtectedRoute>
          }
        />

        {/* Tasting groups - ROOT_ADMIN only */}
        <Route
          path="tasting-groups"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <TastingGroupListPage />
            </RoleProtectedRoute>
          }
        />

        <Route
          path="alcohol-attributes"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <AlcoholAttributeVariantA />
            </RoleProtectedRoute>
          }
        />

        <Route
          path="alcohol-attributes/:variant"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <Navigate to="/alcohol-attributes" replace />
            </RoleProtectedRoute>
          }
        />

        {/* Banners - ROOT_ADMIN only */}
        <Route
          path="banners"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <BannerListPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="banners/new"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <BannerCreatePage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="banners/:id"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <BannerDetailPage />
            </RoleProtectedRoute>
          }
        />
        {/* Campaign contents - ROOT_ADMIN only */}
        <Route
          path="campaign-contents"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <CampaignContentListPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="campaign-contents/new"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <CampaignContentDetailPage key="new" />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="campaign-contents/:id"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <CampaignContentDetailPage key="edit" />
            </RoleProtectedRoute>
          }
        />
        {/* Inquiries - ROOT_ADMIN only */}
        <Route
          path="inquiries"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <InquiryListPage />
            </RoleProtectedRoute>
          }
        />

        {/* Policies - ROOT_ADMIN only */}
        <Route
          path="policies"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <PolicyListPage />
            </RoleProtectedRoute>
          }
        />

        {/* Distilleries - ROOT_ADMIN only */}
        <Route
          path="distilleries"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <DistilleryListPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="distilleries/new"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <DistilleryDetailPage key="new" />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="distilleries/:id"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <DistilleryDetailPage key="edit" />
            </RoleProtectedRoute>
          }
        />

        {/* Regions - ROOT_ADMIN only */}
        <Route
          path="regions"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <RegionListPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="regions/new"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <RegionDetailPage key="new" />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="regions/:id"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <RegionDetailPage key="edit" />
            </RoleProtectedRoute>
          }
        />

        {/* Users - ROOT_ADMIN only */}
        <Route
          path="users"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <UserListPage />
            </RoleProtectedRoute>
          }
        />

        {/* Reviews - ROOT_ADMIN only */}
        <Route
          path="reviews"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <ReviewListPage />
            </RoleProtectedRoute>
          }
        />

        {/* MFDS import declarations - ROOT_ADMIN only */}
        <Route
          path="mfds/declarations"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <MfdsDeclarationListPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="mfds/declarations/:declarationId"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <MfdsDeclarationDetailPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="mfds/importers"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <MfdsImporterListPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="mfds/importers/new"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <MfdsImporterDetailPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="mfds/importers/:importerId"
          element={
            <RoleProtectedRoute roles={['ROOT_ADMIN']}>
              <MfdsImporterDetailPage />
            </RoleProtectedRoute>
          }
        />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
